import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router';
import Alert from '@amzn/awsui-components-console/alert';
import Badge from '@amzn/awsui-components-console/badge';
import BreadcrumbGroup from '@amzn/awsui-components-console/breadcrumb-group';
import Button from '@amzn/awsui-components-console/button';
import Flashbar, {
  FlashbarProps,
} from '@amzn/awsui-components-console/flashbar';
import Spinner from '@amzn/awsui-components-console/spinner';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import { useDraftState } from './hooks/useDraftState';
import { useStringGroupData } from './hooks/useStringGroupData';
import { useSubmitTranslation } from './hooks/useSubmitTranslation';
import { useVersionHistory } from './hooks/useVersionHistory';
import PageHeader from './components/PageHeader';
import TranslationProgressPanel from './components/TranslationProgressPanel';
import MetadataPanel from './components/MetadataPanel';
import StringsTable from './components/StringsTable';
import CreateStringModal from './components/CreateStringModal';
import EditStringModal from './components/EditStringModal';
import EditGroupModal from './components/EditGroupModal';
import SubmitCancelActionBar from './components/SubmitCancelActionBar';
import CancelTranslationModal from './components/CancelTranslationModal';
import SubmitTranslationModal from './components/SubmitTranslationModal';
import VersionHistoryPanel from './components/VersionHistoryPanel';
import DiffModal from './components/DiffModal';
import ViewVersionModal from './components/ViewVersionModal';
import RollbackModal from './components/RollbackModal';
import {
  DraftStringInput,
  GroupEdits,
  NewStringGroupMetadata,
  StringInfo,
  TranslationStatus,
} from './types';
import { getClientDisplayName, isLangHubClient } from './utils/clientIdMapper';
import { getAppSyncErrorType } from 'src/api/graphqlErrorType';
import { useNamespacePermissions } from 'src/common/hooks/useNamespacePermissions';
import {
  STRING_GROUP_NOT_READY_HEADER,
  STRING_GROUP_NOT_READY_MESSAGE,
} from './constants';
import styles from './StringGroupDetail.module.scss';

const FLASHBAR_AUTO_DISMISS_MS = 5000;

/** Shape of the router navigation state this page reads. */
interface StringGroupLocationState {
  newStringGroup?: NewStringGroupMetadata;
}

/**
 * Narrows `useLocation().state` (typed `any`/`unknown` by the router) to the
 * only entry this page consumes, so the cast lives in exactly one place.
 */
function readNewStringGroupState(
  state: unknown,
): NewStringGroupMetadata | undefined {
  return (state as StringGroupLocationState | null)?.newStringGroup;
}

const StringGroupDetailPage: React.FC = () => {
  // Cancellation banner dismiss state (non-persistent — reappears on refresh)
  const [bannerDismissed, setBannerDismissed] = useState(false);
  // Load-error dismissal state (non-persistent — reappears on refresh)
  const [loadErrorDismissed, setLoadErrorDismissed] = useState(false);
  const { namespaceId = '', stringGroupId = '' } = useParams<{
    namespaceId: string;
    stringGroupId: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Metadata handed over by the Create String Group modal via navigation
  // state. Captured once on first render — the history entry's state is
  // cleared below, so a refresh or back/forward visit re-fetches real server
  // data instead of replaying stale form data forever.
  const [newGroupMetadata] = useState<NewStringGroupMetadata | null>(() => {
    const candidate = readNewStringGroupState(location.state);
    // Guard against metadata left on a history entry for a different group.
    return candidate &&
      candidate.stringGroupId === stringGroupId &&
      candidate.namespaceId === namespaceId
      ? candidate
      : null;
  });

  useEffect(() => {
    if (readNewStringGroupState(location.state)) {
      // Same pathname, state stripped: consumes the metadata from the history
      // entry without remounting the page (the copy captured above lives on
      // in component state for this mount).
      navigate(location.pathname, { replace: true, state: null });
    }
    // Mount-only: the metadata is consumed exactly once per page visit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    data: group,
    strings,
    loading: groupLoading,
    error: groupError,
    apolloError,
    refetch: refetchGroup,
  } = useStringGroupData(namespaceId, stringGroupId, newGroupMetadata);

  // Called up here, alongside the other data hooks, because the page returns early
  // for the loading and error states below — a hook next to the `canEdit` line it
  // feeds would be a conditional hook call.
  const permissions = useNamespacePermissions(namespaceId);

  const {
    draftState,
    hasPendingChanges,
    isStaleVersion,
    addString,
    editString,
    deleteString,
    editGroup,
    clearDraft,
  } = useDraftState(namespaceId, stringGroupId, group?.version ?? 0);

  const { submit, loading: submitLoading } = useSubmitTranslation(
    namespaceId,
    stringGroupId,
    draftState,
    group?.cxIds ?? [],
  );

  // Modal visibility state
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editGroupModalVisible, setEditGroupModalVisible] = useState(false);
  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [submitModalVisible, setSubmitModalVisible] = useState(false);
  // True while a successful submission is being finalized: the mutation has
  // resolved but we're still refetching the newly-published version before
  // clearing the draft. Keeps the Submit button disabled/loading across that
  // window so the group can't be double-submitted between the two steps.
  const [finalizingSubmit, setFinalizingSubmit] = useState(false);
  const [editingString, setEditingString] = useState<StringInfo | null>(null);
  const [editingIsDraftAdded, setEditingIsDraftAdded] = useState(false);

  // Version history panel state
  const [versionHistoryVisible, setVersionHistoryVisible] = useState(false);
  const {
    data: versionHistoryData,
    loading: versionHistoryLoading,
    error: versionHistoryError,
  } = useVersionHistory(versionHistoryVisible, namespaceId, stringGroupId);

  // Step 7: Diff/View/Rollback modal state
  const [diffModalVisible, setDiffModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [rollbackModalVisible, setRollbackModalVisible] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(0);

  // Flashbar messages
  const [flashItems, setFlashItems] = useState<
    FlashbarProps.MessageDefinition[]
  >([]);

  // Auto-dismiss timer refs — clean up on unmount
  const autoDismissTimers = useRef<ReturnType<typeof setTimeout>[]>([]);
  useEffect(() => {
    const timers = autoDismissTimers.current;
    return () => {
      timers.forEach(clearTimeout);
    };
  }, []);

  /**
   * Adds a flashbar item with auto-dismiss for success messages.
   */
  const addFlashItem = useCallback((item: FlashbarProps.MessageDefinition) => {
    const id = item.id ?? `flash-${Date.now()}`;
    const newItem: FlashbarProps.MessageDefinition = {
      ...item,
      id,
      dismissible: true,
      dismissLabel: 'Dismiss',
      onDismiss: () => setFlashItems((prev) => prev.filter((f) => f.id !== id)),
    };

    setFlashItems((prev) => [...prev, newItem]);

    // Auto-dismiss success messages after 5 seconds
    if (item.type === 'success') {
      const timer = setTimeout(() => {
        setFlashItems((prev) => prev.filter((f) => f.id !== id));
      }, FLASHBAR_AUTO_DISMISS_MS);
      autoDismissTimers.current.push(timer);
    }
  }, []);

  // Step 8: Navigation guard — warn on unload when there are pending changes
  useEffect(() => {
    if (!hasPendingChanges) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Legacy browsers (older Chrome/Edge) only show the confirmation prompt
      // when returnValue is set, so set it alongside preventDefault().
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasPendingChanges]);

  const handleCreateClick = useCallback(() => {
    setCreateModalVisible(true);
  }, []);

  const handleEditClick = useCallback(
    (stringInfo: StringInfo) => {
      // If this string has been edited in the draft, show draft values instead of server values
      const draftEdited = draftState?.edited.find(
        (e) => e.stringId === stringInfo.stringId,
      );
      const draftAdded = draftState?.added.find(
        (a) => a.stringId === stringInfo.stringId,
      );
      const draftVersion = draftEdited ?? draftAdded;

      if (draftVersion) {
        setEditingString({
          ...stringInfo,
          sourceText: draftVersion.sourceText,
          contextNote: draftVersion.contextNote,
          inlineMarkupType: draftVersion.inlineMarkupType,
          messageFormatType: draftVersion.messageFormatType,
          variants: draftVersion.variants ?? stringInfo.variants,
        });
      } else {
        setEditingString(stringInfo);
      }
      setEditingIsDraftAdded(!!draftAdded);
      setEditModalVisible(true);
    },
    [draftState],
  );

  const handleCreateSubmit = useCallback(
    (strings: DraftStringInput[]) => {
      for (const s of strings) {
        addString(s);
      }
    },
    [addString],
  );

  const handleCreateSuccess = useCallback(
    (count: number) => {
      addFlashItem({
        type: 'success',
        content: `${count} string(s) created successfully. Submit the group for translation to make them available.`,
        id: `create-success-${Date.now()}`,
      });
    },
    [addFlashItem],
  );

  const handleEditSave = useCallback(
    (draft: DraftStringInput, originalStringId?: string) => {
      // If the string ID was renamed (draft-added string only), remove the old
      // entry and add the new one instead of an in-place update.
      if (originalStringId && originalStringId !== draft.stringId) {
        deleteString(originalStringId);
        addString(draft);
      } else {
        editString(draft);
      }
      addFlashItem({
        type: 'success',
        content: `String "${draft.stringId}" updated successfully.`,
        id: `edit-success-${Date.now()}`,
      });
    },
    [editString, deleteString, addString, addFlashItem],
  );

  const handleEditGroupSave = useCallback(
    (edits: GroupEdits) => {
      editGroup(edits);
      addFlashItem({
        type: 'success',
        content: 'String group metadata updated successfully.',
        id: `group-edit-success-${Date.now()}`,
      });
    },
    [editGroup, addFlashItem],
  );

  const handleSubmit = useCallback(() => {
    setSubmitModalVisible(true);
  }, []);

  /**
   * Polls describeStringGroup until the newly-published `targetVersion` is
   * queryable. TIS ingests a submission asynchronously, so the first refetch
   * after submit frequently returns the prior version (or a transient
   * not-ready error). Retries on a short backoff and resolves true once the
   * server reports a version >= target, or false if the budget elapses first.
   * When `targetVersion` is undefined (mutation returned no version) a single
   * refetch is performed and treated as success.
   */
  const waitForPublishedVersion = useCallback(
    async (targetVersion: number | undefined): Promise<boolean> => {
      const observed = await refetchGroup();
      if (targetVersion === undefined) return true;
      if (observed !== undefined && observed >= targetVersion) return true;

      // ~12s total across five retries — long enough to cover typical TIS
      // ingestion without pinning the UI in a loading state for minutes.
      const backoffMs = [1000, 2000, 3000, 3000, 3000];
      for (const delay of backoffMs) {
        await new Promise((resolve) => setTimeout(resolve, delay));
        const next = await refetchGroup();
        if (next !== undefined && next >= targetVersion) return true;
      }
      return false;
    },
    [refetchGroup],
  );

  const handleSubmitConfirm = useCallback(
    async (notificationEmail: string) => {
      setSubmitModalVisible(false);
      const result = await submit(notificationEmail);
      if (!result.success) {
        addFlashItem({
          type: 'error',
          content: result.error ?? 'Failed to submit group for translation.',
          id: `submit-error-${Date.now()}`,
        });
        return;
      }

      // Keep the draft (and therefore the user's edited/added rows) on screen
      // while we poll for the newly-published version. Clearing the draft before
      // the fresh server data is queryable would blank the edits for the whole
      // ingestion window — the reported bug. finalizingSubmit keeps the Submit
      // button disabled/loading across the poll so the group can't be
      // double-submitted meanwhile.
      const targetVersion =
        result.version !== undefined ? Number(result.version) : undefined;
      setFinalizingSubmit(true);
      const reflected = await waitForPublishedVersion(targetVersion);
      // Now safe to clear: the refreshed server snapshot already reflects the
      // submitted changes (or, on timeout, we surface an explicit "still
      // processing" notice below so the user understands the transient gap).
      clearDraft();
      setFinalizingSubmit(false);

      if (reflected) {
        addFlashItem({
          type: 'success',
          content:
            result.version !== undefined
              ? `Group submitted for translation successfully. New version: v${result.version}.`
              : 'Group submitted for translation successfully.',
          id: `submit-success-${Date.now()}`,
        });
      } else {
        // Submission succeeded but TIS has not finished ingesting the new
        // version yet, so it is not queryable. Reassure the user their changes
        // were saved and will appear once processing completes. `reflected` is
        // only false when a concrete target version was polled for — an
        // undefined version short-circuits to success in
        // waitForPublishedVersion — so result.version is always defined here.
        addFlashItem({
          type: 'info',
          content: `Group submitted for translation successfully (v${result.version}). Your changes are still being processed and will appear here shortly — refresh in a few minutes if they haven't.`,
          id: `submit-processing-${Date.now()}`,
        });
      }
    },
    [submit, clearDraft, addFlashItem, waitForPublishedVersion],
  );

  const handleCancelTranslation = useCallback(() => {
    setCancelModalVisible(true);
  }, []);

  const handleCancelSuccess = useCallback(() => {
    setCancelModalVisible(false);
    // Refetch so translationStatus/translationProgress (and the banner and
    // action-bar states derived from them) reflect the cancellation.
    refetchGroup();
    addFlashItem({
      type: 'success',
      content:
        'Translation Cancelled. Rollback to Earlier Version Manually If Required.',
      id: `cancel-success-${Date.now()}`,
    });
  }, [addFlashItem, refetchGroup]);

  const handleVersionHistoryToggle = useCallback(() => {
    setVersionHistoryVisible((prev) => !prev);
  }, []);

  // Step 7: Version history action handlers
  const handleVersionDiff = useCallback((version: number) => {
    setSelectedVersion(version);
    setDiffModalVisible(true);
  }, []);

  const handleVersionView = useCallback((version: number) => {
    setSelectedVersion(version);
    setViewModalVisible(true);
  }, []);

  const handleVersionRollback = useCallback((version: number) => {
    setSelectedVersion(version);
    setRollbackModalVisible(true);
  }, []);

  const handleRollbackSuccess = useCallback(
    (newVersion: number) => {
      setRollbackModalVisible(false);
      refetchGroup();
      // Re-open version history panel to refetch updated history
      if (versionHistoryVisible) {
        setVersionHistoryVisible(false);
        // Toggle back on next tick so the lazy query re-fires
        setTimeout(() => setVersionHistoryVisible(true), 0);
      }
      addFlashItem({
        type: 'success',
        content: `Rollback submitted for translation as v${newVersion}. Translation will begin automatically.`,
        id: `rollback-success-${Date.now()}`,
      });
    },
    [addFlashItem, refetchGroup, versionHistoryVisible],
  );

  // Gate the spinner on the initial load only. useStringGroupData is
  // cache-and-network, so `loading` flips true again on every refetch() the
  // submit / cancel / rollback handlers fire — blanking the page would unmount
  // the Flashbar carrying the success message they just queued.
  if (groupLoading && !group) {
    return <Spinner size="large" />;
  }

  // Detect the transient "string group not ready" condition — a 409 from the
  // backend that means TIS is still ingesting the newly submitted version. Show
  // a distinct retryable dialog instead of the generic error panel. AppSync
  // reports the exception name as a top-level `errorType` (not under
  // `extensions`), so read it via getAppSyncErrorType.
  const isStringGroupNotReady = apolloError?.graphQLErrors?.some(
    (e) => getAppSyncErrorType(e) === 'StringGroupNotReadyException',
  );

  if (!group && isStringGroupNotReady) {
    // A fresh submission is still being ingested by TIS (backend 409). The
    // publish itself succeeded — the version just isn't queryable yet — so
    // surface a calm info flash (matching the standard "submitted successfully,
    // may take a moment to appear" treatment) rather than an error panel.
    //
    // This is a full-page state (there is no group data to fall back to) and,
    // unlike the terminal load-error case below, it is transient and
    // recoverable. So it is intentionally NOT dismissible: the Refresh action
    // is the single recovery path, and letting the user dismiss the flash would
    // strand them on a blank page with no way back short of a browser reload.
    return (
      <Flashbar
        items={[
          {
            type: 'info',
            header: STRING_GROUP_NOT_READY_HEADER,
            content: STRING_GROUP_NOT_READY_MESSAGE,
            action: (
              <Button
                onClick={() => refetchGroup()}
                ariaLabel="Refresh page data"
              >
                Refresh
              </Button>
            ),
            id: 'not-ready-info',
          },
        ]}
      />
    );
  }

  if (!group) {
    // Once dismissed, unmount the Flashbar entirely (rather than rendering it
    // with empty items) so it is removed immediately without a lingering exit
    // transition.
    if (loadErrorDismissed) return null;
    return (
      <Flashbar
        items={[
          {
            type: 'error',
            content: groupError?.message ?? 'Failed to load string group data.',
            dismissible: true,
            dismissLabel: 'Dismiss',
            onDismiss: () => setLoadErrorDismissed(true),
            id: 'load-error',
          },
        ]}
      />
    );
  }

  // String groups owned by a non-LangHub client (e.g. AST) are surfaced
  // read-only: all edit operations (create/edit strings, edit group, submit,
  // cancel, rollback) are disabled.
  //
  // Two independent conditions, both required. Client ownership says whether this
  // group is editable AT ALL — LangHub cannot write to another client's source.
  // SOURCE_MANAGEMENT says whether THIS CALLER may write to this namespace. Neither
  // implies the other: a LangHub-owned group is still read-only for a viewer without
  // the permission, and the permission does not make an AST-owned group writable.
  const isEditableClient = isLangHubClient(group.clientId);
  const canEdit = isEditableClient && permissions.canManageSource;

  // Collect existing string IDs for uniqueness validation in create modal
  const existingStringIds = strings.map((s) => s.stringId);
  const draftAddedStringIds = (draftState?.added ?? []).map((s) => s.stringId);

  return (
    <SpaceBetween size="l">
      <BreadcrumbGroup
        items={[
          { text: 'Home', href: '/' },
          {
            text: group.namespaceId,
            href: `/${group.namespaceId}`,
          },
          { text: group.stringGroupId, href: '' },
        ]}
        // Keep navigation client-side (SPA) instead of a full page reload. The
        // href values are preserved so middle-click / Cmd+click still open in a
        // new tab. The current-page (last) item is rendered as inert plain text
        // by BreadcrumbGroup, so onFollow only fires for the linked items.
        onFollow={(e) => {
          e.preventDefault();
          navigate(e.detail.href);
        }}
      />

      {flashItems.length > 0 && <Flashbar items={flashItems} />}

      {group.translationStatus === TranslationStatus.Cancelled &&
        !bannerDismissed && (
          <Alert
            type="warning"
            dismissible
            dismissAriaLabel="Dismiss cancellation warning"
            onDismiss={() => setBannerDismissed(true)}
          >
            This version was cancelled. Some translations may be missing.
          </Alert>
        )}

      <PageHeader
        title={group.stringGroupId}
        onVersionHistoryClick={handleVersionHistoryToggle}
        onEditGroupClick={() => setEditGroupModalVisible(true)}
        canEdit={canEdit}
        permissionsLoading={permissions.loading}
        statusBadge={
          hasPendingChanges ? <Badge color="blue">Unsaved changes</Badge> : null
        }
      />

      {/*
        Keyed on client ownership, NOT on `canEdit`. The banner's copy explains
        ownership ("owned by AST and is read-only in LangHub"), which says nothing
        true about a caller who simply lacks SOURCE_MANAGEMENT — on a LangHub-owned
        group it would read "owned by LangHub and is read-only in LangHub". A missing
        permission disables the controls and adds no copy, by decision.
      */}
      {!isEditableClient && (
        <Alert type="info">
          This string group is owned by {getClientDisplayName(group.clientId)}{' '}
          and is read-only in LangHub. Editing, submitting for translation, and
          cancelling translation are disabled.
        </Alert>
      )}

      {isStaleVersion && (
        <Alert type="warning">
          Your edits were made against an older version. Please review before
          submitting.
        </Alert>
      )}

      <div className={styles.progressMetadataRow}>
        <div className={styles.progressContainer}>
          <TranslationProgressPanel progress={group.translationProgress} />
        </div>
        <div className={styles.metadataContainer}>
          <MetadataPanel group={group} />
        </div>
      </div>

      <VersionHistoryPanel
        visible={versionHistoryVisible}
        stringGroupName={group.stringGroupId}
        versions={versionHistoryData}
        loading={versionHistoryLoading}
        error={versionHistoryError}
        canEdit={canEdit}
        permissionsLoading={permissions.loading}
        onClose={handleVersionHistoryToggle}
        onDiff={handleVersionDiff}
        onView={handleVersionView}
        onRollback={handleVersionRollback}
      />

      <SubmitCancelActionBar
        hasPendingChanges={hasPendingChanges}
        translationStatus={group.translationStatus}
        translationProgress={group.translationProgress}
        submitLoading={submitLoading || finalizingSubmit}
        canEdit={canEdit}
        isEditableClient={isEditableClient}
        permissionsLoading={permissions.loading}
        onSubmit={handleSubmit}
        onCancel={handleCancelTranslation}
      />

      {/* Strings and metadata arrive together from a single describeStringGroup
          call, so by this point (past the groupLoading/groupError guards) the
          strings snapshot is available. */}
      <StringsTable
        strings={strings}
        clientId={group.clientId}
        namespaceId={namespaceId}
        stringGroupId={stringGroupId}
        draftState={draftState}
        canEdit={canEdit}
        permissionsLoading={permissions.loading}
        onCreateClick={handleCreateClick}
        onEditClick={handleEditClick}
      />

      {/* Modals */}
      <CreateStringModal
        visible={createModalVisible}
        sourceLocale={group.sourceLocale}
        existingStringIds={existingStringIds}
        draftAddedStringIds={draftAddedStringIds}
        onSubmit={handleCreateSubmit}
        onDismiss={() => setCreateModalVisible(false)}
        onSuccess={handleCreateSuccess}
      />

      <EditStringModal
        visible={editModalVisible}
        stringInfo={editingString}
        sourceLocale={group.sourceLocale}
        isDraftAdded={editingIsDraftAdded}
        existingStringIds={existingStringIds}
        draftAddedStringIds={draftAddedStringIds}
        onSave={handleEditSave}
        onDismiss={() => {
          setEditModalVisible(false);
          setEditingString(null);
          setEditingIsDraftAdded(false);
        }}
      />

      <EditGroupModal
        visible={editGroupModalVisible}
        group={group}
        draftEdits={draftState?.groupEdits}
        onSave={handleEditGroupSave}
        onDismiss={() => setEditGroupModalVisible(false)}
      />

      <CancelTranslationModal
        visible={cancelModalVisible}
        namespaceId={namespaceId}
        stringGroupId={stringGroupId}
        version={Number(group.version)}
        onDismiss={() => setCancelModalVisible(false)}
        onSuccess={handleCancelSuccess}
      />

      <SubmitTranslationModal
        visible={submitModalVisible}
        loading={submitLoading}
        onDismiss={() => setSubmitModalVisible(false)}
        onConfirm={handleSubmitConfirm}
      />

      <DiffModal
        visible={diffModalVisible}
        version={selectedVersion}
        namespaceId={namespaceId}
        stringGroupId={stringGroupId}
        onDismiss={() => setDiffModalVisible(false)}
      />

      <ViewVersionModal
        visible={viewModalVisible}
        version={selectedVersion}
        namespaceId={namespaceId}
        stringGroupId={stringGroupId}
        versionMeta={versionHistoryData?.find(
          (v) => Number(v.version) === selectedVersion,
        )}
        onDismiss={() => setViewModalVisible(false)}
      />

      <RollbackModal
        visible={rollbackModalVisible}
        version={selectedVersion}
        currentVersion={Number(group.version)}
        namespaceId={namespaceId}
        stringGroupId={stringGroupId}
        cxIds={group.cxIds}
        isCurrentVersionCancelled={
          group.translationStatus === TranslationStatus.Cancelled
        }
        onDismiss={() => setRollbackModalVisible(false)}
        onSuccess={handleRollbackSuccess}
      />
    </SpaceBetween>
  );
};

export default StringGroupDetailPage;
